import { DiscordSDK } from "@discord/embedded-app-sdk";
import { colorForUser, type Participant } from "@worktools/shared";

export type Session = {
  user: Participant;
  channelId: string;
  guildId: string | null;
  accessToken: string | null;
  inDiscord: boolean;
  channelName: string;
};

const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;

function avatarUrl(userId: string, avatar: string | null): string | null {
  return avatar ? `https://cdn.discordapp.com/avatars/${userId}/${avatar}.png?size=64` : null;
}

/** Standalone browser fallback so the canvas can be developed outside Discord. */
function localSession(): Session {
  // sessionStorage keeps every browser tab a separate guest, which makes local
  // multiplayer testing outside Discord realistic.
  const stored = sessionStorage.getItem("worktools:localUser");
  const id = stored ?? `local-${Math.random().toString(36).slice(2, 8)}`;
  if (!stored) sessionStorage.setItem("worktools:localUser", id);
  const name = sessionStorage.getItem("worktools:localName") ?? `Gast ${id.slice(-4)}`;
  const params = new URLSearchParams(location.search);
  return {
    user: { id, name, avatarUrl: null, color: colorForUser(id) },
    channelId: params.get("channel") ?? "local-dev",
    guildId: null,
    accessToken: null,
    inDiscord: false,
    channelName: "Lokale Entwicklung",
  };
}

export async function startSession(): Promise<Session> {
  const isEmbedded = new URLSearchParams(location.search).has("frame_id");
  if (!isEmbedded || !CLIENT_ID) return localSession();

  const sdk = new DiscordSDK(CLIENT_ID);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: CLIENT_ID,
    response_type: "code",
    state: "",
    prompt: "none",
    scope: ["identify", "guilds.members.read", "rpc.activities.write"],
  });

  const response = await fetch("/api/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error("Discord-Login fehlgeschlagen");
  const { access_token } = (await response.json()) as { access_token: string };

  const auth = await sdk.commands.authenticate({ access_token });
  if (!auth) throw new Error("Discord-Authentifizierung fehlgeschlagen");

  let channelName = "Voice-Channel";
  try {
    if (sdk.channelId) {
      const channel = await sdk.commands.getChannel({ channel_id: sdk.channelId });
      channelName = channel.name ?? channelName;
    }
  } catch {
    // getChannel needs guild permissions we may not have; the fallback name is fine.
  }

  const user = auth.user;
  return {
    user: {
      id: user.id,
      name: user.global_name ?? user.username,
      avatarUrl: avatarUrl(user.id, user.avatar ?? null),
      color: colorForUser(user.id),
    },
    channelId: sdk.channelId ?? `dm-${user.id}`,
    guildId: sdk.guildId ?? null,
    accessToken: access_token,
    inDiscord: true,
    channelName,
  };
}
